import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ListChecks, Trash2, Loader2, CheckCircle2, StickyNote } from "lucide-react";
import { fmtDate } from "@/lib/format";

const prioColor = { high: "bg-red-500/20 text-red-300", medium: "bg-amber-500/20 text-amber-300", low: "bg-sky-500/20 text-sky-300" };
const statusColor = { todo: "bg-white/10 text-muted-foreground", in_progress: "bg-emerald-500/20 text-emerald-300", done: "bg-emerald-600/30 text-emerald-200" };

function NotesEditor({ task, onSave }) {
  const [val, setVal] = useState(task.notes || "");
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); await onSave(val); setBusy(false); };
  return (
    <div className="mt-3">
      <Label className="text-[11px] text-muted-foreground flex items-center gap-1"><StickyNote className="w-3 h-3" /> Notes</Label>
      <Textarea rows={2} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Add a progress note…"
        className="mt-1 bg-[#0A0D0C] border-emerald-500/20 text-sm" data-testid="task-notes-input" />
      {val !== (task.notes || "") && (
        <Button size="sm" variant="outline" className="mt-2 border-emerald-500/30 h-7 text-xs" onClick={save} disabled={busy} data-testid="task-notes-save">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save note"}
        </Button>
      )}
    </div>
  );
}

export default function Tasks() {
  const { user } = useAuth();
  const isMgr = user?.role === "manager" || user?.role === "super_admin";
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assigned_to: "", priority: "medium", due_date: "" });

  const load = async () => {
    setTasks((await api.get("/tasks")).data);
    if (isMgr) setEmployees((await api.get("/employees")).data.filter((e) => e.status !== "disabled"));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/tasks", { ...form, due_date: form.due_date || undefined });
      toast.success("Task created.");
      setOpen(false); setForm({ title: "", description: "", assigned_to: "", priority: "medium", due_date: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const updateProgress = async (t, progress) => {
    setTasks((cur) => cur.map((x) => (x.id === t.id ? { ...x, progress } : x)));
    try { await api.patch(`/tasks/${t.id}`, { progress }); } catch {}
  };
  const commitProgress = async () => { await load(); };
  const setStatus = async (t, status) => { await api.patch(`/tasks/${t.id}`, { status }); load(); };
  const saveNotes = async (t, notes) => { try { await api.patch(`/tasks/${t.id}`, { notes }); toast.success("Note saved."); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };
  const remove = async (t) => { await api.delete(`/tasks/${t.id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-emerald-400" />
          <h1 className="font-display text-2xl font-bold">Tasks</h1>
        </div>
        {isMgr && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1" /> New task</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121816] border-emerald-500/20">
              <DialogHeader><DialogTitle>Assign a task</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="task-title-input" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="task-desc-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Assign to</Label>
                    <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="task-assignee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-[#121816] border-emerald-500/20">
                        {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121816] border-emerald-500/20">
                        {["low", "medium", "high"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="task-due-input" /></div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={busy || !form.title || !form.assigned_to} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="task-create-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create task"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {tasks.length === 0 ? (
        <div className="card-emerald p-12 text-center text-muted-foreground">No tasks yet.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {tasks.map((t) => {
            const mine = t.assigned_to === user.id;
            return (
            <div key={t.id} className="card-emerald p-5" data-testid="task-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{t.title}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{t.description}</p>
                </div>
                <Badge className={prioColor[t.priority]}>{t.priority}</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-muted-foreground">
                <span data-testid="task-assignee">→ {t.assignee_name}</span>
                <span data-testid="task-assigner">· assigned by {t.created_by}</span>
                {t.due_date && <span>· due {t.due_date}</span>}
                <Badge className={`${statusColor[t.status]} ml-auto`}>{t.status.replace("_", " ")}</Badge>
              </div>
              {t.completion_date && (
                <p className="text-xs text-emerald-300 mt-2 flex items-center gap-1" data-testid="task-completion-date">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Completed {fmtDate(t.completion_date)}
                </p>
              )}
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs mb-2"><span className="text-muted-foreground">Progress</span><span>{t.progress}%</span></div>
                <Progress value={t.progress} className="h-2 bg-white/5" />
                {(!isMgr || mine) && (
                  <div className="mt-3">
                    <Slider value={[t.progress]} max={100} step={5}
                      onValueChange={(v) => updateProgress(t, v[0])} onValueCommit={commitProgress}
                      data-testid="task-progress-slider" />
                  </div>
                )}
              </div>
              {(mine || isMgr) && <NotesEditor task={t} onSave={(n) => saveNotes(t, n)} />}
              <div className="flex gap-2 mt-4">
                {t.status !== "done" && (
                  <Button size="sm" variant="outline" className="border-emerald-500/30" onClick={() => setStatus(t, "done")} data-testid="task-complete-btn">Mark done</Button>
                )}
                {isMgr && <Button size="sm" variant="ghost" className="text-red-400 ml-auto" onClick={() => remove(t)} data-testid="task-delete-btn"><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
